import { useState } from 'react';
import type { CardDetailDto, CardPatch } from '@konvert/shared';
import { formatDateRu, formatMinutesRu } from '@konvert/domain';
import { Sheet } from './Sheet.js';
import { CardEditForm } from './CardEditForm.js';
import { t } from '../lib/i18n.js';

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

const ENVELOPE_TITLES: Record<string, string> = {
  day: 'конверте дня',
  week: 'Неделе',
  month: 'Месяце',
  year: 'Годе',
  inbox: 'Бэклоге',
  someday: 'Someday',
  projects: 'Проектах',
  done: 'Копилке',
  trash: 'Корзине',
  notes_archive: 'Архиве заметок',
  carryover: 'Разборе',
};

export function CardSheet({
  card,
  open,
  timezone,
  onClose,
  onComplete,
  onMove,
  onDrop,
  onAddNote,
  onWaiting,
  onSave,
}: {
  card: CardDetailDto | null;
  open: boolean;
  timezone: string;
  onClose: () => void;
  onComplete: () => void;
  onMove: () => void;
  onDrop: () => void;
  onAddNote: (text: string) => void;
  onWaiting: (waitingFor: string, checkOn: string) => void;
  onSave: (patch: CardPatch, expectedVersion: number) => void;
}): JSX.Element {
  const [note, setNote] = useState('');
  const [waitingFor, setWaitingFor] = useState('');
  const [editing, setEditing] = useState(false);

  return (
    <Sheet open={open && card !== null} onClose={onClose} title={card?.title ?? ''}>
      {card && editing ? (
        <CardEditForm
          card={card}
          timezone={timezone}
          onCancel={() => setEditing(false)}
          onSave={(patch) => {
            onSave(patch, card.version);
            setEditing(false);
          }}
        />
      ) : null}

      {card && !editing ? (
        <div className="space-y-4">
          {card.body ? <p className="whitespace-pre-wrap text-sm text-ink">{card.body}</p> : null}

          <div className="flex flex-wrap gap-2 text-xs text-ink-soft">
            <span className="rounded-full border border-[var(--line)] px-2 py-0.5">
              {t(`kind.${card.kind}` as 'kind.task')}
            </span>
            {card.est_minutes ? <span>{formatMinutesRu(card.est_minutes)}</span> : null}
            {card.energy ? <span>{t(`energy.${card.energy}` as 'energy.green')}</span> : null}
            {card.deadline_date ? <span>дедлайн {formatDateRu(card.deadline_date)}</span> : null}
            {card.reschedule_count > 0 ? (
              <span>
                {t('card.rescheduled')} {card.reschedule_count}
              </span>
            ) : null}
            {card.is_stalled ? <span className="text-brick">завис</span> : null}
          </div>

          {card.kind === 'meeting' && card.start_at && card.end_at ? (
            <p className="text-sm text-ink">
              {formatDateRu(card.start_at.slice(0, 10))}, {timeOf(card.start_at)}–{timeOf(card.end_at)}
              {card.location ? ` · ${card.location}` : ''}
            </p>
          ) : null}

          {card.kind === 'reminder' && card.remind_date ? (
            <p className="text-sm text-ink">
              {formatDateRu(card.remind_date)}
              {card.remind_time ? `, ${card.remind_time.slice(0, 5)}` : ''}
            </p>
          ) : null}

          {card.waiting_for ? (
            <p className="text-sm text-ink">Ждём: {card.waiting_for}</p>
          ) : null}

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="paper-card px-3 py-2.5 text-ink"
            >
              {t('card.edit')}
            </button>
            <button type="button" onClick={onComplete} className="paper-card px-3 py-2.5 text-ink">
              {t('card.done')}
            </button>
            <button type="button" onClick={onMove} className="paper-card px-3 py-2.5 text-ink">
              {t('card.move')}
            </button>
            <button type="button" onClick={onDrop} className="paper-card px-3 py-2.5 text-ink">
              {t('card.trash')}
            </button>
            <button
              type="button"
              onClick={() => {
                const checkOn = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
                onWaiting(waitingFor || 'ответа', checkOn);
              }}
              className="paper-card px-3 py-2.5 text-ink"
            >
              {t('card.waiting')}
            </button>
          </div>

          {card.status === 'waiting' ? (
            <input
              value={waitingFor}
              onChange={(event) => setWaitingFor(event.target.value)}
              placeholder="Чего ждём"
              className="w-full rounded-[var(--radius-card)] border border-[var(--line)] bg-paper px-3 py-2 text-sm text-ink outline-none"
            />
          ) : null}

          <section>
            <h3 className="mb-2 text-xs uppercase tracking-wide text-ink-soft">
              {t('card.notes')}
            </h3>
            <ul className="space-y-2">
              {card.notes.map((entry) => (
                <li key={entry.id} className="paper-card px-3 py-2">
                  <p className="text-sm text-ink">{entry.text}</p>
                  <p className="mt-1 text-xs text-ink-soft">
                    {t('card.lay_in')}:{' '}
                    {entry.envelope?.period_start
                      ? formatDateRu(entry.envelope.period_start)
                      : ENVELOPE_TITLES[entry.envelope?.kind ?? ''] ?? '—'}
                  </p>
                </li>
              ))}
            </ul>
            <div className="mt-2 flex gap-2">
              <input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder={t('card.add_note')}
                className="flex-1 rounded-[var(--radius-card)] border border-[var(--line)] bg-paper px-3 py-2 text-sm text-ink outline-none"
              />
              <button
                type="button"
                onClick={() => {
                  if (!note.trim()) return;
                  onAddNote(note.trim());
                  setNote('');
                }}
                className="rounded-[var(--radius-card)] bg-[var(--ink)] px-3 py-2 text-sm text-[var(--paper)]"
              >
                +
              </button>
            </div>
          </section>

          {card.focus_dates.length > 0 ? (
            <section>
              <h3 className="mb-1 text-xs uppercase tracking-wide text-ink-soft">
                {t('card.focus_dates')}
              </h3>
              <p className="text-sm text-ink-soft">
                {card.focus_dates.map((date) => formatDateRu(date)).join(' · ')}
              </p>
            </section>
          ) : null}
        </div>
      ) : null}
    </Sheet>
  );
}
